// src/react/high-level/SlotMovable.tsx
import { useState, useMemo as useMemo3, useEffect as useEffect2 } from "react";
import { proxy, useSnapshot } from "valtio";
import { usePopper } from "react-popper";

// src/react/low-level/components/GameHUD/ItemSlot.tsx
import { useMemo as useMemo2 } from "react";
import { css as css3 } from "@emotion/css";

// src/react/low-level/styles.ts
var zIndexes = {
  canvas: 5,
  hud: 10,
  modal: 1250
};
var css = String.raw;
var fullScreenFixedStyles = css`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
`;
var modalStyles = css`
    ${fullScreenFixedStyles}
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: ${zIndexes.modal};
`;
var buttonStyles = css`
    padding: 0;
    background: transparent;
    font-family: inherit;
    letter-spacing: 0.5px;
    border: none;
    color: white;
    text-transform: uppercase;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;
var pixelatedImage = css`
    image-rendering: crisp-edges;
    image-rendering: pixelated;
`;
var focusableElemOutline = css`
    outline: 2px solid dodgerblue;
`;

// src/react/low-level/components/GameHUD/HotbarBlockModel.tsx
import { useEffect, useMemo, useRef } from "react";
import anime from "animejs";
import clsx from "clsx";
import reactUseMeasure from "react-use-measure";
import { css as css2 } from "@emotion/css";
import { ResizeObserver } from "@juggle/resize-observer";
import { jsx } from "react/jsx-runtime";
var blockSides = ["front", "left", "top"];
var rotateFunctions = {
  front: "",
  left: "rotateY(-90deg)",
  top: "rotateX(90deg)"
};
var sidesBrightness = {
  front: 0.5,
  left: 0.7,
  top: 1
};
var makeBlockSides = (texture) => blockSides.map((side) => [side, texture]);
var HotbarBlockModel = ({
  sideTextures: sidesTexture,
  RootDivProps = {},
  RotatbleDivProps = {},
  rotateX = 327,
  rotateY = 405
}) => {
  const [rootRef, { width }] = reactUseMeasure({ polyfill: ResizeObserver });
  const rotatingBlockRef = useRef(null);
  useEffect(() => {
    const rotatingBlock = rotatingBlockRef.current;
    anime.set(rotatingBlock, {
      rotateX
      // scale: sizeFactor
    });
    if (rotateY !== "animate") {
      anime.set(rotatingBlock, {
        rotateY
      });
      return;
    }
    anime({
      targets: rotatingBlock,
      rotateY: "360deg",
      duration: 6e3,
      easing: "linear",
      loop: true
    });
  }, []);
  const sidesTextureNormalized = useMemo(
    () => typeof sidesTexture === "string" ? makeBlockSides(sidesTexture) : Object.entries(sidesTexture),
    [sidesTexture]
  );
  return /* @__PURE__ */ jsx(
    "div",
    {
      ...RootDivProps,
      ref: rootRef,
      className: clsx(
        "BlockModel",
        css2`
                    width: 100%;
                    height: 100%;
                    box-sizing: content-box;
                    position: relative;
                `
      ),
      children: /* @__PURE__ */ jsx(
        "div",
        {
          ...RotatbleDivProps,
          ref: rotatingBlockRef,
          className: css2`
                    transform-style: preserve-3d;
                    width: 100%;
                    height: 100%;
                `,
          children: sidesTextureNormalized.map(([side, textureUrl]) => {
            if (side !== "top" && side !== "front" && side !== "left")
              return null;
            return /* @__PURE__ */ jsx(
              "div",
              {
                style: {
                  transform: `${rotateFunctions[side]} translateZ(${width / 2}px)`
                },
                className: css2`
                                filter: brightness(${sidesBrightness[side]});
                                position: absolute;
                                width: 100%;
                                height: 100%;
                            `,
                children: /* @__PURE__ */ jsx(
                  "img",
                  {
                    src: textureUrl,
                    alt: "",
                    draggable: "false",
                    className: css2`
                                    width: 100%;
                                    height: 100%;
                                    ${pixelatedImage}
                                `
                  }
                )
              },
              side
            );
          })
        }
      )
    }
  );
};
var HotbarBlockModel_default = HotbarBlockModel;

// src/react/low-level/components/GameHUD/ItemSlot.tsx
import { jsx as jsx2, jsxs } from "react/jsx-runtime";
var ItemSlot = ({ blocksPadding = 9, data: rawSlotData }) => {
  const fontSize = useMemo2(() => blocksPadding <= 6 ? 10 : 15, [blocksPadding]);
  const slotData = useMemo2(() => {
    if (!rawSlotData)
      return null;
    if (rawSlotData.type === "item")
      return rawSlotData;
    return {
      ...rawSlotData,
      sideTextures: Object.fromEntries(blockSides.map((side) => [side, rawSlotData.getTexture(side)]))
    };
  }, [rawSlotData]);
  return slotData && /* @__PURE__ */ jsxs(
    "div",
    {
      style: {
        padding: slotData.type === "block" ? blocksPadding : ""
      },
      className: css3`
                    overflow: hidden;
                    position: relative;
                    width: 100%;
                    height: 100%;
                `,
      children: [
        slotData.type === "item" ? /* @__PURE__ */ jsx2(
          "img",
          {
            alt: "Item",
            src: slotData.texture,
            className: css3`
                            ${pixelatedImage}
                            width: 100%;
                            height: 100%;
                        `
          }
        ) : /* @__PURE__ */ jsx2(HotbarBlockModel_default, { sideTextures: slotData.sideTextures }),
        /* @__PURE__ */ jsx2(
          "span",
          {
            className: css3`
                        position: absolute;
                        bottom: 0;
                        right: 0;
                        font-size: ${fontSize}px;
                        color: white;
                    `,
            children: slotData.count !== 1 && slotData.count.toString()
          }
        )
      ]
    }
  );
};
var ItemSlot_default = ItemSlot;

// src/react/high-level/SlotMovable.tsx
import { jsx as jsx3, jsxs as jsxs2 } from "react/jsx-runtime";
var hoverFocusableProxy = proxy({
  value: false
});
addEventListener("keydown", ({ altKey }) => {
  if (altKey) {
    hoverFocusableProxy.value = true;
  }
});
addEventListener("keyup", ({ altKey }) => {
  if (altKey) {
    hoverFocusableProxy.value = false;
  }
});
var lastClickEventCoords;
var draggingSlot = proxy({
  slot: void 0
});
var SLOT_SIZE = 32;
var getItemData = (name) => {
  const mcData = window.mcData ?? McData(window.currentVersion);
  const mcAssets = window.mcAssets ?? McAssets(window.currentVersion);
  const data = mcData.itemsByName[name];
  const isBlock = !!mcData.blocksByName[name];
  const texturePath = `${isBlock ? "blocks" : "items"}/${(isBlock ? mcAssets.blocksModels[name]?.textures?.side?.replace(/^(minecraft:)?block\//, "") : void 0) || name}`;
  return { ...data, isBlock, texturePath };
};
var textureBase = window.textureBase ?? "https://raw.githubusercontent.com/PrismarineJS/minecraft-assets/master/data/1.16.4";
var DraggingSlot = () => {
  const [draggingEl, setDraggingEl] = useState(null);
  const { slot: draggingItem } = useSnapshot(draggingSlot);
  useEffect2(() => {
    const el = draggingEl;
    if (!el)
      return;
    const controller = new AbortController();
    document.addEventListener(
      "pointermove",
      (e) => {
        el.style.left = e.clientX - SLOT_SIZE / 2 + "px";
        el.style.top = e.clientY - SLOT_SIZE / 2 + "px";
      },
      {
        signal: controller.signal
      }
    );
    document.dispatchEvent(new MouseEvent("pointermove", lastClickEventCoords));
    setTimeout(() => {
      document.addEventListener(
        "pointerdown",
        (e) => {
          const el2 = e.target.closest(".slot");
          if (!el2)
            return;
          const { slotIndex } = el2.dataset;
          if (!slotIndex || isNaN(+slotIndex))
            return;
          draggingItem.action(draggingItem.index, +slotIndex, draggingItem.count);
          draggingSlot.slot = void 0;
          controller.abort();
        },
        {
          signal: controller.signal
        }
      );
    });
  }, [draggingEl]);
  if (!draggingItem)
    return null;
  return /* @__PURE__ */ jsx3(
    "div",
    {
      style: {
        position: "fixed",
        pointerEvents: "none",
        width: SLOT_SIZE,
        height: SLOT_SIZE,
        zIndex: 100
      },
      ref: setDraggingEl,
      children: /* @__PURE__ */ jsx3(ItemSlot_default, { ...{ ...getSlotDataProps(draggingItem.itemData) } })
    }
  );
};
var getSlotDataProps = (itemData) => {
  return {
    blocksPadding: 6,
    data: itemData.isBlock ? {
      type: "block",
      // todo
      count: itemData.count,
      getTexture(side) {
        return `${textureBase}/${itemData.texturePath}.png`;
      }
    } : {
      type: "item",
      texture: `${textureBase}/${itemData.texturePath}.png`,
      count: itemData.count
    }
  };
};
var SlotMovable_default = ({
  item,
  action
}) => {
  const [referenceElement, setReferenceElement] = useState(null);
  const [popperElement, setPopperElement] = useState(null);
  const [arrowElement, setArrowElement] = useState(null);
  const [hovered, setHovered] = useState(false);
  const hoverFocusable = useSnapshot(hoverFocusableProxy);
  const { slot: draggingItem } = useSnapshot(draggingSlot);
  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    modifiers: [{ name: "arrow", options: { element: arrowElement, padding: 8 } }],
    placement: "top"
  });
  const { drag: actionDrag } = typeof action === "object" ? action : { drag: void 0 };
  const itemData = useMemo3(() => {
    return item && { ...getItemData(item.name), count: item.count };
  }, [item]);
  if (actionDrag?.index !== void 0 && draggingItem?.index === actionDrag?.index)
    return null;
  return /* @__PURE__ */ jsxs2(
    "div",
    {
      "data-slot-index": actionDrag?.index,
      style: {
        width: SLOT_SIZE,
        aspectRatio: "1",
        /* background: SLOT_BG, border: '1px solid black' */
        marginRight: 3,
        marginBottom: 3
      },
      className: "slot",
      onPointerOver: () => setHovered(true),
      onPointerOut: () => setHovered(false),
      children: [
        itemData && hovered && // todo make proxy, only one in state
        /* @__PURE__ */ jsxs2(
          "div",
          {
            ref: setPopperElement,
            style: {
              ...styles.popper,
              color: "white",
              background: "black",
              userSelect: "text",
              pointerEvents: hoverFocusable.value ? void 0 : "none",
              zIndex: 100
            },
            ...attributes.popper,
            children: [
              itemData.displayName,
              " ",
              itemData.id && `(#${itemData.id})`,
              /* @__PURE__ */ jsx3("div", { ref: setArrowElement, style: styles.arrow })
            ]
          }
        ),
        item && /* @__PURE__ */ jsx3(
          "div",
          {
            ref: setReferenceElement,
            style: {
              width: SLOT_SIZE,
              height: SLOT_SIZE
            },
            onPointerDown: (event) => {
              if (!action)
                return;
              if (draggingSlot.slot)
                return;
              const { clientX, clientY } = event;
              lastClickEventCoords = { clientX, clientY };
              const { index } = action.drag;
              const { count, name } = { ...item };
              draggingSlot.slot = {
                index,
                count,
                itemData,
                action: actionDrag?.onMoved
              };
            },
            children: /* @__PURE__ */ jsx3(
              ItemSlot_default,
              {
                ...{
                  ...getSlotDataProps(itemData)
                }
              }
            )
          }
        )
      ]
    }
  );
};

// src/react/low-level/state.ts
import { useMemo as useMemo4 } from "react";
import { useMedia } from "react-use";
import createStore from "zustand";
import { subscribeWithSelector } from "zustand/middleware";
var getInitialState = () => ({
  uiCustomization: {
    /** if true, touch controls will be used */
    forceTouchControls: false,
    touchButtonSize: 50,
    touchButtonsGap: 0,
    hotbarSlotsGap: 0,
    maxHotbarSlotSize: 45,
    touchTimeToDropItem: 600,
    touchTimeToDropItemStack: 1500
  },
  movement: {
    x: 0,
    y: 0,
    z: 0
  },
  hotbar: {
    selectedSlot: 0,
    slots: []
  },
  isFlying: {
    // TODO disallow these defaults
    current: false,
    updatable: true,
    enabled: true
  },
  openedUI: null,
  usingRawInput: null
});
var useInterfaceState = createStore(subscribeWithSelector(() => getInitialState()));
var initInterfaceState = (data) => {
  useInterfaceState.setState(data);
};
var useUsingTouch = () => {
  const { forceTouchControls } = useInterfaceState((state) => state.uiCustomization);
  const touchSupportedQuery = useMedia(`(pointer: coarse)`);
  return useMemo4(
    () => forceTouchControls || touchSupportedQuery || // workaround for chromium bug. Just try to comment this line and use select element feature (CTRL+SHIFT+C) with active mobile view
    navigator.userAgent.includes("Mobile"),
    [forceTouchControls, touchSupportedQuery]
  );
};

// src/react/low-level/components/GameHUD/TouchControls/index.tsx
import { useMemo as useMemo5, useState as useState3 } from "react";
import { css as css5 } from "@emotion/css";

// src/react/low-level/components/GameHUD/TouchControls/MovementButton.tsx
import clsx2 from "clsx";
import { css as css4 } from "@emotion/css";

// src/react/low-level/components/GameHUD/TouchControls/svg/MovementButton.svgr.svg
import * as React3 from "react";
import { jsx as jsx4 } from "react/jsx-runtime";
function SvgMovementButtonsvgr(props) {
  return /* @__PURE__ */ jsx4("svg", { xmlns: "http://www.w3.org/2000/svg", viewBox: "0 0 960 960", ...props, children: /* @__PURE__ */ jsx4("path", { d: "m480 151.8-376 651.3h752z", strokeWidth: 10, stroke: "#fff", fill: "rgba(0, 0, 0, 0.5)" }) });
}
var MovementButton_svgr_default = SvgMovementButtonsvgr;

// src/react/low-level/components/GameHUD/TouchControls/svg/CircleButton.svgr.svg
import * as React4 from "react";
import { jsx as jsx5 } from "react/jsx-runtime";
function SvgCircleButtonsvgr(props) {
  return /* @__PURE__ */ jsx5("svg", { xmlns: "http://www.w3.org/2000/svg", viewBox: "0 0 200 200", ...props, children: /* @__PURE__ */ jsx5("circle", { cx: 100, cy: 100, r: 80, stroke: "#fff", fill: "none", strokeWidth: 10 }) });
}
var CircleButton_svgr_default = SvgCircleButtonsvgr;

// src/react/low-level/components/GameHUD/TouchControls/util.ts
import { useCallback, useEffect as useEffect3, useState as useState2 } from "react";
var releasePointerCapture = (e) => {
  e.preventDefault();
  e.stopPropagation();
  const target = e.target;
  if (!target.releasePointerCapture)
    return;
  target.releasePointerCapture(e.pointerId);
};
var useFixedPointerEvents = (props) => {
  const [touching, setTouching] = useState2(false);
  const toggleIsTouching = useCallback(
    (newState, event) => {
      if (touching === newState)
        return;
      event?.stopPropagation();
      const relatedTarget = event?.relatedTarget;
      if (event && event.type === "pointerout" && relatedTarget?.parentElement === event.currentTarget)
        return;
      const { updateTouching, startTouching, stopTouching } = props;
      setTouching(newState);
      updateTouching?.(newState);
      (newState ? startTouching : stopTouching)?.(event);
    },
    [touching, props]
  );
  useEffect3(() => {
    if (!touching)
      return;
    const removeTouchIfHidden = () => document.visibilityState === "hidden" && toggleIsTouching(false);
    const onTouchEnd = (e) => e.touches.length === 0 && toggleIsTouching(false);
    document.addEventListener("visibilitychange", removeTouchIfHidden);
    document.documentElement.addEventListener("touchend", onTouchEnd);
    return () => {
      document.removeEventListener("visibilitychange", removeTouchIfHidden);
      document.documentElement.removeEventListener("touchend", onTouchEnd);
    };
  }, [touching, toggleIsTouching]);
  return [
    {
      onPointerDown: releasePointerCapture,
      onPointerOver: (e) => toggleIsTouching(true, e),
      onPointerOut: (e) => toggleIsTouching(false, e),
      // TODO report 20+ bugs where this event isn't firing for some reason
      onPointerCancel: (e) => toggleIsTouching(false, e),
      // for tablets on windows
      onContextMenu: (e) => e.preventDefault()
      // onTouchMove: e => e.stopPropagation(),
      // onTouchStart: e => e.stopPropagation(),
      // onTouchEnd: e => e.stopPropagation(),
      // onTouchCancel: e => e.stopPropagation(),
    },
    touching
  ];
};

// src/react/low-level/components/GameHUD/TouchControls/MovementButton.tsx
import { jsx as jsx6, jsxs as jsxs3 } from "react/jsx-runtime";
var MovementButtonSvgr = MovementButton_svgr_default.ReactComponent ? MovementButton_svgr_default.ReactComponent : MovementButton_svgr_default;
var CircleButtonSvgr = CircleButton_svgr_default.ReactComponent ? CircleButton_svgr_default.ReactComponent : CircleButton_svgr_default;
var touchingButtonClass = css4`
    background-color: rgba(255, 255, 255, 0.1);
`;
var MovementButton = ({ action, children, DivProps, Image = null }) => {
  const [pointerEvents, touching] = useFixedPointerEvents({
    updateTouching: typeof action === "function" ? action : (newState) => {
      const movementActions = typeof action[0] === "string" ? [action] : action;
      const coordsToUpdate = Object.fromEntries(movementActions.map(([coord, step]) => [coord, newState ? step : 0]));
      useInterfaceState.getState().updateCoord?.(Object.entries(coordsToUpdate)[0]);
      useInterfaceState.setState({ movement: coordsToUpdate });
    }
  });
  const imageClassName = Image ? clsx2(
    css4`
                  opacity: 0.9;
              `,
    Image.className
  ) : void 0;
  return /* @__PURE__ */ jsxs3(
    "div",
    {
      "data-name": "MovementButton",
      ...DivProps,
      className: clsx2(
        css4`
                    width: 100%;
                    height: 100%;
                    & > * {
                        pointer-events: none;
                        width: 100%;
                        height: 100%;
                    }
                `,
        {
          [touchingButtonClass]: touching
        },
        DivProps?.className
      ),
      ...pointerEvents,
      children: [
        Image && Image.type === "bundled" ? Image.src === "arrow" ? /* @__PURE__ */ jsx6(
          MovementButtonSvgr,
          {
            className: clsx2(
              css4`
                                background: rgba(128, 128, 128, 0.8);
                            `,
              imageClassName
            )
          }
        ) : /* @__PURE__ */ jsx6(CircleButtonSvgr, { className: imageClassName }) : (
          // TODO
          //@ts-ignore
          /* @__PURE__ */ jsx6("img", { ...Image, className: imageClassName })
        ),
        children
      ]
    }
  );
};
var MovementButton_default = MovementButton;

// src/react/low-level/components/GameHUD/TouchControls/index.tsx
import { Fragment, jsx as jsx7, jsxs as jsxs4 } from "react/jsx-runtime";
var leftControlsConfig = [
  [
    "wa",
    -45,
    [
      ["x", -1],
      ["z", -1]
    ]
  ],
  ["w", 0, ["z", -1]],
  [
    "wd",
    45,
    [
      ["x", 1],
      ["z", -1]
    ]
  ],
  ["d", 90, ["x", 1]],
  ["s", 180, ["z", 1]],
  ["a", 270, ["x", -1]]
];
var TouchMovementArea = ({ templateAreas, children }) => {
  const touchSize = useInterfaceState((store) => store.uiCustomization.touchButtonSize);
  const touchButtonsGap = useInterfaceState((store) => store.uiCustomization.touchButtonsGap);
  const gridTemplateAreas = useMemo5(() => templateAreas.map((s) => `"${s}"`).join(""), [templateAreas]);
  return /* @__PURE__ */ jsx7(
    "div",
    {
      "data-name": "TouchMovementArea",
      className: css5`
                display: grid;
                grid-template-areas: ${gridTemplateAreas};
                gap: ${touchButtonsGap}px;
                grid-auto-columns: ${touchSize}px;
                grid-auto-rows: ${touchSize}px;
            `,
      children
    }
  );
};
var pauseButtonSize = 45;
var PauseButton = () => /* @__PURE__ */ jsx7(
  "img",
  {
    style: {
      position: "fixed",
      top: 0,
      right: 0,
      width: pauseButtonSize,
      height: pauseButtonSize,
      padding: 5,
      backgroundColor: "rgba(255, 255, 255, 0.1)"
    },
    onTouchStart: () => {
      window.dispatchEvent(
        new KeyboardEvent("keydown", {
          code: "Escape"
        })
      );
    }
  }
);
var LeftTouchArea = () => {
  const [showForwardAuxButtons, setShowForwardAuxButtons] = useState3(false);
  return /* @__PURE__ */ jsx7(TouchMovementArea, { templateAreas: ["wa w wd", "a . d", ". s ."], children: leftControlsConfig.map(
    ([label, rotate, action]) => (Math.abs(rotate) !== 45 || showForwardAuxButtons) && /* @__PURE__ */ jsx7(
      MovementButton_default,
      {
        action,
        DivProps: {
          className: css5`
                                    /* border: ${Math.abs(rotate) !== 45 ? "1px solid rgba(255, 255, 255, 0.2)" : ""}; */
                                    grid-area: ${label};
                                `
        },
        Image: {
          type: "bundled",
          src: "arrow",
          className: css5`
                                    transform: rotate(${rotate}deg);
                                `
        }
      },
      label
    )
  ) });
};
var rightControlsConfig = [
  ["u", 0, ["y", 1]],
  ["d", 180, ["y", -1]]
];
var RightTouchArea = () => {
  const isFlying = useInterfaceState((state) => state.isFlying);
  return /* @__PURE__ */ jsx7(TouchMovementArea, { templateAreas: isFlying ? ["u .", "c .", "d ."] : ["c .", "d ."], children: isFlying ? rightControlsConfig.map(([label, rotate, action]) => /* @__PURE__ */ jsx7(
    MovementButton_default,
    {
      action,
      DivProps: {
        className: css5`
                                grid-area: ${label};
                            `
      },
      Image: {
        type: "bundled",
        src: "arrow",
        className: css5`
                                transform: rotate(${rotate}deg);
                            `
      }
    },
    label
  )) : /* @__PURE__ */ jsxs4(Fragment, { children: [
    /* @__PURE__ */ jsx7(
      MovementButton_default,
      {
        action: ["y", 1],
        DivProps: {
          className: css5`
                                grid-area: c;
                                height: 100%;
                            `
        },
        Image: {
          type: "bundled",
          src: "circle"
        }
      }
    ),
    /* @__PURE__ */ jsx7(
      MovementButton_default,
      {
        action: ["y", -1],
        DivProps: {
          className: css5`
                                grid-area: d;
                                height: 100%;
                            `
        },
        Image: {
          type: "bundled",
          src: "circle"
        }
      }
    )
  ] }) });
};

// src/react/high-level/InventoryNew.tsx
import { css as css6 } from "@emotion/css";

// src/react/high-level/inventorySlots.ts
var inventorySlots_default = () => {
  const windowSlotsCoords = {
    inventory: {},
    chest: {},
    "large-chest": {},
    "crafting-table": {},
    furnace: {}
  };
  windowSlotsCoords.inventory = {
    // Crafting slots
    0: [307, 55],
    1: [195, 35],
    2: [231, 35],
    3: [195, 71],
    4: [231, 71],
    5: [15, 15],
    // Helmet
    9: [15, 167],
    // Inventory
    18: [15, 203],
    // Inventory
    27: [15, 239],
    // Inventory
    36: [15, 283],
    // Tool bar
    45: [153, 123]
    // Left hand
  };
  for (let i = 5 + 1; i <= 8; i++) {
    windowSlotsCoords.inventory[i] = [windowSlotsCoords.inventory[i - 1][0], windowSlotsCoords.inventory[i - 1][1] + 36];
  }
  for (let i = 9; i < 5 * 9; i += 9) {
    for (let j = 1; j <= 8; j++) {
      windowSlotsCoords.inventory[i + j] = [windowSlotsCoords.inventory[i][0] + 36 * j, windowSlotsCoords.inventory[i][1]];
    }
  }
  windowSlotsCoords.chest = {
    // Chest slots
    0: [15, 35],
    9: [15, 71],
    18: [15, 107],
    // Inventory slots
    27: [15, 169],
    36: [15, 205],
    45: [15, 241],
    // Tool bar slots
    54: [15, 285]
  };
  for (let i = 0; i < 7 * 9; i += 9) {
    for (let j = 1; j <= 8; j++) {
      windowSlotsCoords.chest[i + j] = [windowSlotsCoords.chest[i][0] + 36 * j, windowSlotsCoords.chest[i][1]];
    }
  }
  windowSlotsCoords["large-chest"] = {
    // Chest slots
    0: [15, 35],
    9: [15, 71],
    18: [15, 107],
    27: [15, 143],
    36: [15, 179],
    45: [15, 215],
    // Inventory slots
    54: [15, 277],
    63: [15, 313],
    72: [15, 349],
    // Tool bar slots
    81: [15, 393]
  };
  for (let i = 0; i < 10 * 9; i += 9) {
    for (let j = 1; j <= 8; j++) {
      windowSlotsCoords["large-chest"][i + j] = [windowSlotsCoords["large-chest"][i][0] + 36 * j, windowSlotsCoords["large-chest"][i][1]];
    }
  }
  windowSlotsCoords["crafting-table"] = {
    0: [239, 61],
    // Output slot
    1: [59, 33],
    // Input slots
    4: [59, 69],
    // Input slots
    7: [59, 105],
    // Input slots
    // Inventory slots
    10: [15, 167],
    19: [15, 203],
    28: [15, 239],
    // Tool bar slots
    37: [15, 283]
  };
  for (let i = 1; i < 3 * 3; i += 3) {
    for (let j = 1; j <= 2; j++) {
      windowSlotsCoords["crafting-table"][i + j] = [
        windowSlotsCoords["crafting-table"][i][0] + 36 * j,
        windowSlotsCoords["crafting-table"][i][1]
      ];
    }
  }
  for (let i = 10; i < 5 * 9; i += 9) {
    for (let j = 1; j <= 8; j++) {
      windowSlotsCoords["crafting-table"][i + j] = [
        windowSlotsCoords["crafting-table"][i][0] + 36 * j,
        windowSlotsCoords["crafting-table"][i][1]
      ];
    }
  }
  windowSlotsCoords.furnace = {
    0: [111, 33],
    // Input slot
    1: [111, 105],
    // Fuel slot
    2: [223, 61],
    // Output slot
    // Inventory slots
    3: [15, 167],
    12: [15, 203],
    21: [15, 239],
    // Tool bar slots
    30: [15, 283]
  };
  for (let i = 3; i < 4 * 9; i += 9) {
    for (let j = 1; j <= 8; j++) {
      windowSlotsCoords.furnace[i + j] = [windowSlotsCoords.furnace[i][0] + 36 * j, windowSlotsCoords.furnace[i][1]];
    }
  }
  return windowSlotsCoords;
};

// node_modules/.pnpm/mineflayer-web-inventory@1.8.4/node_modules/mineflayer-web-inventory/client/public/windows/chest.png
var chest_default = "data:image/png;base64,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";

// node_modules/.pnpm/mineflayer-web-inventory@1.8.4/node_modules/mineflayer-web-inventory/client/public/windows/crafting-table.png
var crafting_table_default = "data:image/png;base64,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";

// node_modules/.pnpm/mineflayer-web-inventory@1.8.4/node_modules/mineflayer-web-inventory/client/public/windows/inventory.png
var inventory_default = "data:image/png;base64,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";

// src/react/high-level/InventoryNew.tsx
import { Portal } from "react-portal";
import { jsx as jsx8, jsxs as jsxs5 } from "react/jsx-runtime";
var slotsPos = inventorySlots_default();
var InventoryNew_default = ({ onClose = void 0, slots, action, ui = "inventory" }) => {
  return /* @__PURE__ */ jsxs5(
    "div",
    {
      className: css6`
                position: fixed;
                inset: 0;
                background-color: rgba(0, 0, 0, 0.5);
                display: flex;
                justify-content: center;
                align-items: center;
            `,
      children: [
        /* @__PURE__ */ jsx8(
          "div",
          {
            onClick: (e) => e.target === e.currentTarget && onClose?.(e),
            className: css6`
                    background-image: url(${ui === "inventory" ? inventory_default : ui === "crafting-table" ? crafting_table_default : chest_default});
                    background-size: cover;
                    width: 352px;
                    height: 332px;
                    position: relative;
                `,
            children: Object.entries(slotsPos[ui]).map(([slot, [x, y]], i) => /* @__PURE__ */ jsx8(
              "div",
              {
                className: css6`
                            position: absolute;
                            inset: ${y + 1}px 0 0 ${x + 1}px;
                            width: 32px;
                            height: 32px;
                            &:hover {
                                background-color: rgba(255, 255, 255, 0.5);
                            }
                        `,
                children: /* @__PURE__ */ jsx8(
                  SlotMovable_default,
                  {
                    item: slots[i],
                    action: {
                      drag: {
                        onMoved: action,
                        index: i
                      }
                    }
                  }
                )
              },
              i
            ))
          }
        ),
        /* @__PURE__ */ jsx8(Portal, { node: document.body, children: /* @__PURE__ */ jsx8(DraggingSlot, {}) })
      ]
    }
  );
};
export {
  DraggingSlot,
  InventoryNew_default as InventoryNew,
  LeftTouchArea,
  PauseButton,
  RightTouchArea,
  SlotMovable_default as SlotMovable,
  initInterfaceState,
  useInterfaceState,
  useUsingTouch
};
